import React from 'react';

import classNames from 'classnames';
import AttachmentIcon from 'icons/attachment.svg';
import ClockIcon from 'icons/clock.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import ActionsPill from 'components/application/ActionsPill';
import OutlinedButton from 'components/application/buttons/OutlinedButton';
import CircleQuestion from 'components/application/CircleQuestion';
import Tooltip from 'components/application/Tooltip';

const tooltippableCourseLength = 50;
const tooltippableCourseLengthWithEvidence = 40;
const tooltippableCourseLengthWithRequiredEvidence = 22;

export default function TrainingName({
  attachments,
  course,
  isHistorical,
  isOverrideActive,
  isRequired,
  onAttachmentsToggleClick,
  showEvidenceRequirement,
  training,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'training.TrainingName',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  let appliesTooltip = false;

  const requiresEvidence =
    !isHistorical &&
    showEvidenceRequirement &&
    course.attributes.requiresEvidence &&
    training;

  if (attachments.length > 0) {
    appliesTooltip =
      course.attributes.name.length > tooltippableCourseLengthWithEvidence;
  } else if (attachments.length === 0 && requiresEvidence) {
    appliesTooltip =
      course.attributes.name.length >
      tooltippableCourseLengthWithRequiredEvidence;
  } else {
    appliesTooltip = course.attributes.name.length > tooltippableCourseLength;
  }

  return (
    <>
      {isHistorical && (
        <div className='tw-mr-1 tw-flex tw-items-center'>
          <ClockIcon
            className='[&_path]:tw-fill-grey-500'
            height={16}
            width={16}
          />
        </div>
      )}
      <div className='truncated-text-container truncated-text-container--auto tooltip-parent'>
        <span
          className={classNames(
            'tw-text-m',
            !isHistorical && 'tw-font-medium tw-text-grey-900',
          )}
        >
          {course.attributes.name}
        </span>

        <p className='tw-mb-0 tw-flex tw-items-center tw-text-xs'>
          {!isRequired && i18nTShared('optional')}
          {isOverrideActive && (
            <span className='tw-ml-0.5 tw-inline-flex tw-items-center tw-font-medium'>
              {!isRequired && ' - '}
              {i18nT('overridden.label')}
              <Tooltip
                className='tooltip-dark--max-w-xxs'
                placement='top'
                tooltip={i18nT('overridden.tooltip')}
                trigger='hover'
                triggerProps={{
                  className: 'tooltip-trigger tw-static tw-ml-1',
                }}
              >
                <CircleQuestion size='sm' />
              </Tooltip>
            </span>
          )}
        </p>
        {appliesTooltip && (
          <Tooltip
            placement='top'
            tooltip={course.attributes.name}
            trigger='hover'
          />
        )}
      </div>
      {attachments.length > 0 ?
        <OutlinedButton
          className='tw-ml-3'
          color='grey'
          onClick={onAttachmentsToggleClick}
          size='xs'
        >
          <AttachmentIcon height={16} width={16} />
          <span className='tw-ml-1.5 tw-text-xs tw-font-semibold tw-tracking-wide'>
            {attachments.length}
          </span>
        </OutlinedButton>
      : requiresEvidence && (
          <div className='tw-ml-3 tw-inline-flex'>
            <ActionsPill
              color='cyan'
              displayText='0'
              icon={<AttachmentIcon height={16} width={16} />}
              tooltipText={i18nT('requiresEvidence.tooltipText')}
            />
          </div>
        )
      }
    </>
  );
}

TrainingName.propTypes = {
  isHistorical: PropTypes.bool,
  training: PropTypes.object,
  attachments: PropTypes.array,
  course: PropTypes.object.isRequired,
  isOverrideActive: PropTypes.bool,
  onAttachmentsToggleClick: PropTypes.func,
};

TrainingName.defaultProps = {
  attachments: [],
  showEvidenceRequirement: true,
};
